import React, { useState, useEffect } from 'react';
import { getWhatsAppConfig, formatPhoneNumberForUrl, subscribeToWhatsAppConfig } from '../services/whatsappService';

function ServicePopup({ onClose }) {
  const [loadingServiceId, setLoadingServiceId] = useState(null);
  const [error, setError] = useState(null);
  const [customerServices, setCustomerServices] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load WhatsApp configuration from database with real-time updates
  useEffect(() => {
    const unsubscribe = subscribeToWhatsAppConfig((configData) => {
      if (configData) {
        // Store full config
        setConfig(configData);
        // Filter out services without phone numbers
        const servicesWithNumbers = (configData.services || []).filter(
          service => service.phoneNumber && service.phoneNumber.trim() !== ''
        );
        setCustomerServices(servicesWithNumbers);
      } else {
        // Fallback to default if database fails
        const defaultConfig = {
          defaultMessage: 'Hello, I need assistance with your service',
          services: [
            { id: '1', label: 'Customer Service 1', phoneNumber: '+14083335900', message: 'Hello, I need assistance with your service' }
          ]
        };
        setConfig(defaultConfig);
        setCustomerServices(defaultConfig.services);
      }
      setLoading(false);
    });
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);


  const handleItemClick = async (service) => {
    // Check if service has phone number
    if (!service.phoneNumber || service.phoneNumber.trim() === '') {
      setError('Phone number not configured for this service');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Set loading state
    setLoadingServiceId(service.id);
    setError(null);

    try {
      // Format phone number for WhatsApp URL (remove + sign)
      const formattedNumber = formatPhoneNumberForUrl(service.phoneNumber);
      
      // Validate phone number
      if (!formattedNumber || formattedNumber.length < 10) {
        throw new Error('Invalid phone number format');
      }
      
      // Get message (use service message if it exists and is not empty, otherwise use config defaultMessage, or fallback)
      const message = (service.message && service.message.trim() !== '') 
        ? service.message.trim() 
        : ((config && config.defaultMessage && config.defaultMessage.trim() !== '') 
          ? config.defaultMessage.trim() 
          : 'Hello, I need assistance with your service');
      
      // Open WhatsApp with pre-filled message
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/${formattedNumber}?text=${encodedMessage}`;
      
      console.log('Message being sent:', message); // Debug log
      console.log('Encoded message:', encodedMessage); // Debug log
      
      console.log('Opening WhatsApp:', whatsappUrl); // Debug log
      
      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Close the popup first
      if (onClose) {
        onClose();
      }
      
      // Try to open WhatsApp - use location.href for better compatibility
      // This works better than window.open which can be blocked by popup blockers
      try {
        // Try window.open first (opens in new tab)
        const newWindow = window.open(whatsappUrl, '_blank');
        
        // If window.open was blocked, use location.href as fallback
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          // Fallback: redirect current page to WhatsApp
          window.location.href = whatsappUrl;
        }
      } catch (openError) {
        // Fallback to location.href if window.open fails
        console.error('Error opening WhatsApp:', openError);
        window.location.href = whatsappUrl;
      }
    } catch (err) {
      // Handle errors
      console.error('Error opening WhatsApp:', err);
      setError(err.message || 'Failed to open WhatsApp. Please try again.');
      setLoadingServiceId(null);
      
      // Show error for 3 seconds, then clear
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };

  if (loading) {
    return (
      <div className="service-popup-overlay" onClick={onClose}>
        <div className="service-popup-content" onClick={(e) => e.stopPropagation()}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-popup-overlay" onClick={onClose}>
      <div className="service-popup-content" onClick={(e) => e.stopPropagation()}>
        {/* Error Message */}
        {error && (
          <div className="alert alert-danger mb-3" style={{ 
            padding: '10px 15px', 
            borderRadius: '8px',
            fontSize: '0.9rem',
            marginBottom: '15px'
          }}>
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Customer Service Options */}
        {customerServices.map((service) => (
          <div
            key={service.id}
            className="service-popup-item"
            onClick={() => !loadingServiceId && handleItemClick(service)}
            style={{ 
              cursor: loadingServiceId ? 'not-allowed' : 'pointer',
              opacity: loadingServiceId && loadingServiceId !== service.id ? 0.5 : 1,
              position: 'relative'
            }}
          >
            <div className="service-popup-item-icon">
              {loadingServiceId === service.id ? (
                <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <i className="bi bi-headset"></i>
              )}
            </div>
            <div className="service-popup-item-label">
              {service.label}
              {loadingServiceId === service.id && (
                <span className="ms-2" style={{ fontSize: '0.85rem', color: '#666' }}>
                  Opening WhatsApp...
                </span>
              )}
            </div>
            <div className="service-popup-item-arrow">
              {loadingServiceId !== service.id && (
                <i className="bi bi-chevron-right"></i>
              )}
            </div>
          </div>
        ))}

        {/* Info Message */}
        <div className="mt-3" style={{ fontSize: '0.75rem', color: '#666', textAlign: 'center', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
          <i className="bi bi-info-circle me-1"></i>
          Click to open WhatsApp chat
        </div>
      </div>
    </div>
  );
}

export default ServicePopup;

